/**
 * Edge Function Utilities
 *
 * Helper functions for working with Supabase Edge Functions,
 * particularly for extracting meaningful error messages from responses.
 */

/**
 * Extracts the actual error message from a Supabase Edge Function error.
 *
 * When an Edge Function returns a non-2xx status code, Supabase throws a
 * FunctionsHttpError with a generic message like "Edge Function returned a non-2xx status code".
 * The actual error message from the response body is available in error.context.
 *
 * @param {Error} error - The error thrown by supabase.functions.invoke()
 * @param {string} fallbackMessage - Default message if extraction fails
 * @returns {Promise<string>} The extracted error message
 *
 * @example
 * const { data, error } = await supabase.functions.invoke('my-function', { body: {...} });
 * if (error) {
 *   const message = await extractEdgeFunctionError(error, 'Operation failed');
 *   return { success: false, error: message };
 * }
 */
export async function extractEdgeFunctionError(error, fallbackMessage = 'An error occurred') {
  if (!error) return fallbackMessage;

  try {
    // For FunctionsHttpError, the response body is available in error.context
    if (error.context && typeof error.context.json === 'function') {
      const errorBody = await error.context.json();
      if (errorBody?.error) {
        return errorBody.error;
      }
      if (errorBody?.message) {
        return errorBody.message;
      }
    }

    // Check if error.message contains useful info (not the generic "non-2xx" message)
    if (error.message && !error.message.includes('non-2xx')) {
      return error.message;
    }
  } catch (parseError) {
    console.error('Failed to parse Edge Function error response:', parseError);
  }

  return fallbackMessage;
}

/**
 * Wraps a Supabase Edge Function call with proper error handling.
 * Returns a consistent { success, data, error } response format.
 *
 * @param {Function} supabase - The Supabase client
 * @param {string} functionName - Name of the Edge Function to invoke
 * @param {Object} body - Request body to send to the function
 * @param {string} errorPrefix - Prefix for error messages (e.g., "Failed to create user")
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 *
 * @example
 * const result = await invokeEdgeFunction(
 *   supabase,
 *   'create-workforce-member',
 *   { email, name, role },
 *   'Failed to create workforce member'
 * );
 *
 * if (!result.success) {
 *   console.error(result.error);
 * }
 */
export async function invokeEdgeFunction(supabase, functionName, body, errorPrefix = 'Operation failed') {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, { body });

    if (error) {
      const errorMessage = await extractEdgeFunctionError(error, errorPrefix);
      return { success: false, error: errorMessage };
    }

    // Handle case where Edge Function returns success: false in the body
    if (data && data.success === false) {
      return { success: false, error: data.error || errorPrefix };
    }

    return { success: true, data };
  } catch (unexpectedError) {
    console.error(`Unexpected error invoking ${functionName}:`, unexpectedError);
    return { success: false, error: unexpectedError.message || errorPrefix };
  }
}
